import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Phone,
  Menu,
  X,
  MapPin,
  Mail,
  ArrowUpRight,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";
import Logo from "@/components/Logo";
import { COMPANY, SOCIALS, addressLines } from "@/data/company";
import {
  PRIMARY_NAV,
  SERVICE_CATEGORIES,
  INDUSTRIES,
  REGIONS,
  TOWNS,
  ABOUT_LINKS,
  ALL_SERVICES,
  LEGAL_LINKS,
} from "@/data/nav";
import { trackPhoneClick, trackPortalClick } from "@/lib/track";
import { openSettings as openCookieSettings } from "@/lib/consent";

const PORTAL_URL = COMPANY.portal;
const PHONE_DISPLAY = COMPANY.phone.display;
const PHONE_TEL = COMPANY.phone.tel;

/* ---------- Mega-menu content builders --------------------------------- */

const ServicesMega = ({ close }) => (
  <div className="grid grid-cols-4 gap-6 p-6">
    {SERVICE_CATEGORIES.map((cat) => (
      <div key={cat.slug}>
        <Link
          to={`/services/${cat.slug}`}
          onClick={close}
          className="block group"
          data-testid={`mega-cat-${cat.slug}`}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[color:var(--lt-blue-soft)] group-hover:text-white transition-colors">
            {cat.name}
          </p>
          <p className="mt-1 text-[12px] text-[color:var(--lt-text-mute)]">
            {cat.tagline}
          </p>
        </Link>
        <ul className="mt-4 space-y-2.5">
          {cat.leaves.map((leaf) => (
            <li key={leaf.slug}>
              <Link
                to={`/services/${leaf.slug}`}
                onClick={close}
                className="text-[13.5px] text-[color:var(--lt-text-dim)] hover:text-white transition-colors block"
                data-testid={`mega-service-${leaf.slug}`}
              >
                {leaf.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

const IndustriesMega = ({ close }) => (
  <div className="grid grid-cols-2 gap-x-8 gap-y-2 p-6">
    {INDUSTRIES.map((i) => (
      <Link
        key={i.slug}
        to={`/industries/${i.slug}`}
        onClick={close}
        className="group flex items-start gap-3 rounded-lg px-3 py-3 hover:bg-white/5 transition-colors"
        data-testid={`mega-industry-${i.slug}`}
      >
        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[color:var(--lt-blue-bright)] flex-shrink-0" />
        <span>
          <span className="block text-[14px] text-white font-semibold">
            {i.name}
          </span>
          <span className="block text-[12px] text-[color:var(--lt-text-mute)] mt-0.5">
            {i.blurb}
          </span>
        </span>
      </Link>
    ))}
  </div>
);

const CoverageMega = ({ close }) => (
  <div className="grid grid-cols-2 gap-6 p-6">
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[color:var(--lt-blue-soft)]">
        Regions
      </p>
      <ul className="mt-4 space-y-2.5">
        {REGIONS.map((r) => (
          <li key={r.slug}>
            <Link
              to={`/coverage/${r.slug}`}
              onClick={close}
              className="text-[13.5px] text-[color:var(--lt-text-dim)] hover:text-white transition-colors block"
              data-testid={`mega-region-${r.slug}`}
            >
              {r.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[color:var(--lt-blue-soft)]">
        Major towns
      </p>
      <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2">
        {TOWNS.slice(0, 12).map((t) => (
          <li key={t.slug}>
            <Link
              to={`/coverage/${t.slug}`}
              onClick={close}
              className="text-[13px] text-[color:var(--lt-text-dim)] hover:text-white transition-colors block"
              data-testid={`mega-town-${t.slug}`}
            >
              {t.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const AboutMega = ({ close }) => (
  <div className="p-6 min-w-[260px]">
    <ul className="space-y-3">
      {ABOUT_LINKS.map((a) => (
        <li key={a.slug || "root"}>
          <Link
            to={a.slug ? `/about/${a.slug}` : "/about"}
            onClick={close}
            className="group block"
            data-testid={`mega-about-${a.slug || "root"}`}
          >
            <span className="block text-[14px] text-white font-semibold group-hover:text-[color:var(--lt-blue-bright)] transition-colors">
              {a.name}
            </span>
            <span className="block text-[12px] text-[color:var(--lt-text-mute)] mt-0.5">
              {a.desc}
            </span>
          </Link>
        </li>
      ))}
      <li className="pt-2 border-t" style={{ borderColor: "var(--lt-border)" }}>
        <a
          href={PORTAL_URL}
          target="_blank"
          rel="noreferrer"
          onClick={() => {
            trackPortalClick("nav_about_menu");
            close();
          }}
          className="group flex items-center gap-2 text-[14px] text-white font-semibold"
          data-testid="mega-about-portal"
        >
          Client Portal
          <ArrowUpRight size={14} className="text-[color:var(--lt-blue-bright)]" />
        </a>
      </li>
    </ul>
  </div>
);

const MEGA_MAP = {
  services: { render: ServicesMega, width: "min(1100px, 92vw)" },
  industries: { render: IndustriesMega, width: "min(720px, 92vw)" },
  coverage: { render: CoverageMega, width: "min(720px, 92vw)" },
  about: { render: AboutMega, width: "min(340px, 92vw)" },
};

/* ---------- Desktop nav item with mega-menu ---------------------------- */

const NavMegaItem = ({ item, activeKey, setActiveKey }) => {
  const isOpen = activeKey === item.key;
  const cfg = MEGA_MAP[item.key];
  const MegaContent = cfg?.render;

  return (
    <div
      className="relative"
      onMouseEnter={() => setActiveKey(item.key)}
      onMouseLeave={() => setActiveKey(null)}
    >
      <NavLink
        to={item.path}
        data-testid={`nav-link-${item.key}`}
        className={({ isActive }) =>
          `text-sm tracking-wide transition-colors inline-flex items-center gap-1 py-2 ${
            isActive || isOpen
              ? "text-white"
              : "text-[color:var(--lt-text-dim)] hover:text-white"
          }`
        }
      >
        {item.label}
        <ChevronDown
          size={13}
          className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </NavLink>

      {isOpen && MegaContent && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full pt-3"
          style={{ width: cfg.width }}
          onMouseEnter={() => setActiveKey(item.key)}
          onMouseLeave={() => setActiveKey(null)}
        >
          <div
            className="rounded-xl overflow-hidden shadow-2xl"
            style={{
              background: "rgba(10, 17, 36, 0.98)",
              border: "1px solid var(--lt-border-strong)",
              backdropFilter: "blur(20px)",
              boxShadow: "0 30px 80px -20px rgba(0, 0, 0, 0.6)",
            }}
            data-testid={`mega-panel-${item.key}`}
          >
            <MegaContent close={() => setActiveKey(null)} />
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------- Header -------------------------------------------------------- */

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 100);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setActiveKey(null);
  }, [pathname]);

  // Body scroll lock when mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <header
      className="sticky top-0 z-50 border-b transition-all"
      style={{
        borderColor: "var(--lt-border)",
        background: scrolled ? "rgba(6, 11, 24, 0.92)" : "rgba(6, 11, 24, 0.78)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
      data-testid="site-header"
    >
      <div
        className="max-w-7xl mx-auto px-5 lg:px-10 flex items-center justify-between transition-all"
        style={{ height: scrolled ? 60 : 72 }}
      >
        <Link to="/" data-testid="header-logo-link" className="block flex-shrink-0">
          <Logo height={scrolled ? 40 : 46} />
        </Link>

        <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
          {PRIMARY_NAV.map((item) =>
            item.type === "mega" ? (
              <NavMegaItem
                key={item.key}
                item={item}
                activeKey={activeKey}
                setActiveKey={setActiveKey}
              />
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-link-${item.label.replace(/\s+/g, "-").toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors py-2 ${
                    isActive
                      ? "text-white"
                      : "text-[color:var(--lt-text-dim)] hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={PHONE_TEL}
            onClick={() => trackPhoneClick("header_desktop")}
            className="inline-flex items-center gap-2 rounded-md px-3.5 py-2 text-[13px] font-semibold transition-transform hover:scale-[1.02]"
            style={{
              background: "rgba(227, 77, 58, 0.14)",
              border: "1px solid rgba(255, 107, 90, 0.42)",
              color: "#ffb3a4",
            }}
            data-testid="header-phone"
            title="24/7 emergency callout"
          >
            <AlertTriangle size={13} />
            24/7 · {PHONE_DISPLAY}
          </a>
          <a
            href={PORTAL_URL}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackPortalClick("header_desktop")}
            className="inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-[13px] font-medium transition-colors"
            style={{
              border: "1px solid var(--lt-border-strong)",
              color: "var(--lt-text-dim)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "white";
              e.currentTarget.style.borderColor = "rgba(127, 166, 255, 0.6)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "var(--lt-text-dim)";
              e.currentTarget.style.borderColor = "var(--lt-border-strong)";
            }}
            data-testid="header-portal-cta"
          >
            Client Portal <ArrowUpRight size={13} />
          </a>
        </div>

        <button
          className="lg:hidden p-2 rounded-md border"
          style={{ borderColor: "var(--lt-border-strong)" }}
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {mobileOpen && <MobileMenu close={() => setMobileOpen(false)} />}
    </header>
  );
};

/* ---------- Mobile menu -------------------------------------------------- */

const MobileMenu = ({ close }) => {
  const [expanded, setExpanded] = useState(null);
  const toggle = (key) => setExpanded((prev) => (prev === key ? null : key));

  return (
    <div
      className="lg:hidden fixed inset-0 top-0 z-[60] overflow-y-auto"
      style={{ background: "rgba(6, 11, 24, 0.98)", backdropFilter: "blur(18px)" }}
      data-testid="mobile-nav"
    >
      <div className="flex items-center justify-between px-5 h-[60px] border-b" style={{ borderColor: "var(--lt-border)" }}>
        <Logo height={40} />
        <button
          onClick={close}
          className="p-2 rounded-md border"
          style={{ borderColor: "var(--lt-border-strong)" }}
          aria-label="Close menu"
          data-testid="mobile-menu-close"
        >
          <X size={20} />
        </button>
      </div>

      <div className="px-5 py-6 pb-40 space-y-3">
        {PRIMARY_NAV.map((item) => {
          if (item.type === "link") {
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={close}
                data-testid={`mobile-nav-link-${item.label.replace(/\s+/g, "-").toLowerCase()}`}
                className={({ isActive }) =>
                  `block text-lg py-3 border-b ${
                    isActive ? "text-white" : "text-[color:var(--lt-text-dim)]"
                  }`
                }
                style={{ borderColor: "var(--lt-border)" }}
              >
                {item.label}
              </NavLink>
            );
          }
          const isOpen = expanded === item.key;
          return (
            <div
              key={item.key}
              className="border-b"
              style={{ borderColor: "var(--lt-border)" }}
              data-testid={`mobile-accordion-${item.key}`}
            >
              <button
                type="button"
                onClick={() => toggle(item.key)}
                className="w-full flex items-center justify-between py-3 text-lg text-white text-left"
                aria-expanded={isOpen}
              >
                {item.label}
                <ChevronRight
                  size={18}
                  className={`transition-transform ${isOpen ? "rotate-90" : ""} text-[color:var(--lt-blue-soft)]`}
                  aria-hidden="true"
                />
              </button>
              {isOpen && (
                <div className="pb-4 pl-2 space-y-2">
                  <MobileAccordionContent kind={item.key} close={close} />
                  <Link
                    to={item.path}
                    onClick={close}
                    className="inline-flex items-center gap-1 text-[13px] text-[color:var(--lt-blue-bright)] mt-2"
                  >
                    View all {item.label.toLowerCase()} <ArrowUpRight size={13} />
                  </Link>
                </div>
              )}
            </div>
          );
        })}

        <div className="pt-4">
          <a
            href={PORTAL_URL}
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              trackPortalClick("header_mobile");
              close();
            }}
            className="inline-flex items-center gap-2 text-[15px] font-semibold text-white"
            data-testid="mobile-portal-cta"
          >
            Client Portal <ArrowUpRight size={15} />
          </a>
        </div>
      </div>

      {/* Fixed bottom call CTA in mobile menu */}
      <div
        className="fixed bottom-0 inset-x-0 p-4 border-t"
        style={{
          borderColor: "var(--lt-border)",
          background: "rgba(6, 11, 24, 0.98)",
          backdropFilter: "blur(14px)",
        }}
      >
        <a
          href={PHONE_TEL}
          onClick={() => {
            trackPhoneClick("mobile_menu_bottom");
            close();
          }}
          className="w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-3.5 text-sm font-semibold"
          style={{
            background: "#e34d3a",
            color: "white",
            boxShadow: "0 10px 30px -10px rgba(227, 77, 58, 0.6)",
          }}
          data-testid="mobile-menu-call-cta"
        >
          <Phone size={16} /> Call 24/7 · {PHONE_DISPLAY}
        </a>
      </div>
    </div>
  );
};

const MobileAccordionContent = ({ kind, close }) => {
  if (kind === "services") {
    return (
      <>
        {SERVICE_CATEGORIES.map((cat) => (
          <div key={cat.slug} className="mt-2">
            <Link
              to={`/services/${cat.slug}`}
              onClick={close}
              className="text-[13px] font-semibold uppercase tracking-[0.2em] text-[color:var(--lt-blue-soft)]"
            >
              {cat.name}
            </Link>
            <ul className="mt-1.5 space-y-1.5">
              {cat.leaves.map((leaf) => (
                <li key={leaf.slug}>
                  <Link
                    to={`/services/${leaf.slug}`}
                    onClick={close}
                    className="text-[14px] text-[color:var(--lt-text-dim)] block py-1"
                  >
                    {leaf.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </>
    );
  }
  if (kind === "industries") {
    return (
      <ul className="space-y-1.5">
        {INDUSTRIES.map((i) => (
          <li key={i.slug}>
            <Link
              to={`/industries/${i.slug}`}
              onClick={close}
              className="text-[14px] text-[color:var(--lt-text-dim)] block py-1"
            >
              {i.name}
            </Link>
          </li>
        ))}
      </ul>
    );
  }
  if (kind === "coverage") {
    return (
      <>
        <p className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--lt-blue-soft)]">Regions</p>
        <ul className="space-y-1.5 mb-3">
          {REGIONS.map((r) => (
            <li key={r.slug}>
              <Link
                to={`/coverage/${r.slug}`}
                onClick={close}
                className="text-[14px] text-[color:var(--lt-text-dim)] block py-1"
              >
                {r.name}
              </Link>
            </li>
          ))}
        </ul>
        <p className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--lt-blue-soft)] mt-3">Towns</p>
        <ul className="space-y-1.5">
          {TOWNS.map((t) => (
            <li key={t.slug}>
              <Link
                to={`/coverage/${t.slug}`}
                onClick={close}
                className="text-[14px] text-[color:var(--lt-text-dim)] block py-1"
              >
                {t.name}
              </Link>
            </li>
          ))}
        </ul>
      </>
    );
  }
  if (kind === "about") {
    return (
      <ul className="space-y-1.5">
        {ABOUT_LINKS.map((a) => (
          <li key={a.slug || "root"}>
            <Link
              to={a.slug ? `/about/${a.slug}` : "/about"}
              onClick={close}
              className="text-[14px] text-[color:var(--lt-text-dim)] block py-1"
            >
              {a.name}
            </Link>
          </li>
        ))}
      </ul>
    );
  }
  return null;
};

/* ---------- Sticky mobile call bar (site-wide, visible on scroll) ------- */

const StickyMobileCallBar = () => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Hide on /contact — form is right there
  if (pathname.startsWith("/contact")) return null;

  return (
    <div
      className={`lg:hidden fixed inset-x-0 bottom-0 z-40 border-t p-3 transition-transform duration-200 ${
        visible ? "translate-y-0" : "translate-y-full"
      }`}
      style={{
        borderColor: "var(--lt-border)",
        background: "rgba(6, 11, 24, 0.96)",
        backdropFilter: "blur(14px)",
        paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))",
      }}
      data-testid="sticky-mobile-call-bar"
    >
      <div className="flex gap-2">
        <a
          href={PHONE_TEL}
          onClick={() => trackPhoneClick("sticky_mobile_bar")}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-md py-3 text-[13px] font-semibold"
          style={{
            background: "#e34d3a",
            color: "white",
          }}
          data-testid="sticky-call"
        >
          <Phone size={14} /> Call 24/7
        </a>
        <Link
          to="/contact"
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-md py-3 text-[13px] font-semibold btn-primary"
          data-testid="sticky-enquire"
        >
          Enquire
        </Link>
      </div>
    </div>
  );
};

/* ---------- Footer (5-column sitemap) ----------------------------------- */

const Footer = () => (
  <footer
    className="mt-32 border-t"
    style={{ borderColor: "var(--lt-border)", background: "rgba(6, 11, 24, 0.6)" }}
    data-testid="site-footer"
  >
    <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
      {/* Top: brand + 5 columns */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
        <div className="col-span-2 md:col-span-3 lg:col-span-2">
          <Logo height={52} />
          <p className="mt-5 text-sm text-[color:var(--lt-text-dim)] max-w-md leading-relaxed">
            Lutrom Engineering Ltd — mechanical, electrical, controls and bespoke
            machinery services for industrial and commercial sites across the North
            West of England. 24/7 on-call. ISO 9001, 14001 and 45001 certified.
          </p>
        </div>

        <div>
          <p className="eyebrow mb-4">Services</p>
          <ul className="space-y-2 text-[13.5px]">
            {ALL_SERVICES.slice(0, 12).map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  className="text-[color:var(--lt-text-dim)] hover:text-white transition-colors"
                  data-testid={`footer-service-${s.slug}`}
                >
                  {s.name}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/services"
                className="text-[color:var(--lt-blue-soft)] hover:text-white transition-colors inline-flex items-center gap-1"
              >
                All services <ArrowUpRight size={12} />
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <p className="eyebrow mb-4">Industries</p>
          <ul className="space-y-2 text-[13.5px]">
            {INDUSTRIES.map((i) => (
              <li key={i.slug}>
                <Link
                  to={`/industries/${i.slug}`}
                  className="text-[color:var(--lt-text-dim)] hover:text-white transition-colors"
                  data-testid={`footer-industry-${i.slug}`}
                >
                  {i.name}
                </Link>
              </li>
            ))}
          </ul>

          <p className="eyebrow mb-4 mt-6">Coverage</p>
          <ul className="space-y-2 text-[13.5px]">
            {REGIONS.map((r) => (
              <li key={r.slug}>
                <Link
                  to={`/coverage/${r.slug}`}
                  className="text-[color:var(--lt-text-dim)] hover:text-white transition-colors"
                  data-testid={`footer-region-${r.slug}`}
                >
                  {r.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="eyebrow mb-4">Company</p>
          <ul className="space-y-2 text-[13.5px]">
            <li><Link to="/about" className="text-[color:var(--lt-text-dim)] hover:text-white">About Lutrom</Link></li>
            <li><Link to="/about/team" className="text-[color:var(--lt-text-dim)] hover:text-white">The Team</Link></li>
            <li><Link to="/about/quality-and-compliance" className="text-[color:var(--lt-text-dim)] hover:text-white">Quality &amp; Compliance</Link></li>
            <li><Link to="/about/accreditations" className="text-[color:var(--lt-text-dim)] hover:text-white">Accreditations</Link></li>
            <li><Link to="/about/careers" className="text-[color:var(--lt-text-dim)] hover:text-white">Careers</Link></li>
            <li><Link to="/case-studies" className="text-[color:var(--lt-text-dim)] hover:text-white">Case Studies</Link></li>
            <li><Link to="/insights" className="text-[color:var(--lt-text-dim)] hover:text-white">Insights</Link></li>
            <li><Link to="/contact" className="text-[color:var(--lt-text-dim)] hover:text-white">Contact</Link></li>
            <li>
              <a
                href={PORTAL_URL}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackPortalClick("footer")}
                className="text-[color:var(--lt-blue-soft)] hover:text-white transition-colors inline-flex items-center gap-1"
                data-testid="footer-link-portal"
              >
                Client Portal <ArrowUpRight size={12} />
              </a>
            </li>
          </ul>
        </div>

        <div>
          <p className="eyebrow mb-4">Contact</p>
          <ul className="space-y-3 text-[13.5px] text-[color:var(--lt-text-dim)]">
            <li className="flex items-start gap-2">
              <Phone size={15} className="mt-0.5 text-[color:var(--lt-blue-soft)] flex-shrink-0" />
              <a href={PHONE_TEL} onClick={() => trackPhoneClick("footer")} className="hover:text-white" data-testid="footer-phone">
                {PHONE_DISPLAY}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Mail size={15} className="mt-0.5 text-[color:var(--lt-blue-soft)] flex-shrink-0" />
              <a href={COMPANY.email.mailto} className="hover:text-white break-all" data-testid="footer-email">
                {COMPANY.email.display}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin size={15} className="mt-0.5 text-[color:var(--lt-blue-soft)] flex-shrink-0" />
              <span data-testid="footer-address">
                {addressLines().map((line) => (
                  <span key={line} className="block">{line}</span>
                ))}
              </span>
            </li>
          </ul>

          <p className="eyebrow mt-6 mb-3">Follow</p>
          <ul className="flex items-center gap-2" data-testid="footer-socials">
            {SOCIALS.map((s) => {
              const Icon = s.icon;
              return (
                <li key={s.name}>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={s.name}
                    className="inline-flex items-center justify-center rounded-md transition-colors"
                    style={{
                      width: 36,
                      height: 36,
                      background: "rgba(15, 26, 51, 0.7)",
                      border: "1px solid var(--lt-border-strong)",
                      color: "var(--lt-text)",
                    }}
                    data-testid={`footer-social-${s.name.toLowerCase()}`}
                  >
                    <Icon size={15} />
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Accreditations strip */}
      <div className="mt-12 pt-8 border-t flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] uppercase tracking-[0.22em] text-[color:var(--lt-text-mute)]"
        style={{ borderColor: "var(--lt-border)" }}
      >
        <span>ISO 9001</span><span>•</span>
        <span>ISO 14001</span><span>•</span>
        <span>ISO 45001</span><span>•</span>
        <span>SafeContractor</span><span>•</span>
        <span>IPAF</span><span>•</span>
        <span>City &amp; Guilds</span>
      </div>
    </div>

    <div className="divider-line" />
    <div className="max-w-7xl mx-auto px-5 lg:px-10 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-[color:var(--lt-text-mute)]">
      <p>© {new Date().getFullYear()} {COMPANY.legalName}. All rights reserved.</p>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        {LEGAL_LINKS.map((l) => (
          <Link key={l.path} to={l.path} className="hover:text-white transition-colors" data-testid={`footer-legal-${l.path.slice(1)}`}>
            {l.name}
          </Link>
        ))}
        <a
          href="/sitemap.xml"
          className="hover:text-white transition-colors"
          data-testid="footer-sitemap-link"
        >
          Sitemap
        </a>
        <button
          type="button"
          onClick={openCookieSettings}
          className="hover:text-white transition-colors underline-offset-2 hover:underline"
          data-testid="footer-cookie-settings"
        >
          Cookie settings
        </button>
        <span className="hidden md:inline">·</span>
        <span>{COMPANY.registered} · Company No. {COMPANY.companyNumber}</span>
      </div>
    </div>
  </footer>
);

/* ---------- Layout root -------------------------------------------------- */

const Layout = ({ children }) => (
  <div className="min-h-screen flex flex-col" style={{ background: "var(--lt-bg)" }}>
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
    <StickyMobileCallBar />
  </div>
);

export default Layout;
